'use client';

import { useEffect, useRef } from 'react';
import { sendGTMEvent } from '../sendGTMEvent';
import { useBasket } from '~/components/features/Basket';
import { productListItemToGAItem } from '../productMappers/productListItemToGAItem';
import { getSelectItemData } from '../itemSelectionUtils';
import { useGTMQueue } from '../useGTMQueue';
import { getBasketFinancials } from '../utils/basketUtils';
import { CommerceModel } from 'api-types';

/**
 * Tracks the view_cart event when users view their shopping cart/basket.
 *
 * This component should be rendered on the basket/cart page to track when users
 * view their cart contents. It sends a GA4 view_cart event with the current
 * basket contents including item details, total value, and currency.
 *
 * The event is triggered on first render and whenever the basket quantity changes,
 * ensuring accurate tracking of cart viewing behavior.
 *
 * @returns null - This is a tracking component that doesn't render anything
 */

export const ViewCartTracker = () => {
  const prevQuantity = useRef<number | null>(null);
  const { data: basket, isLoading } = useBasket();
  const { addToQueue } = useGTMQueue();

  // Calculate total quantity from basket lines
  const totalQuantity = basket?.lines?.reduce((sum, line) => sum + (line.quantity || 0), 0) ?? 0;

  useEffect(() => {
    const shouldNotTrack = isLoading || totalQuantity === prevQuantity.current;

    if (shouldNotTrack) {
      return;
    }

    addToQueue(() => {
      sendViewCartEvent(basket);
    });

    prevQuantity.current = totalQuantity;
  }, [totalQuantity, isLoading]);

  return null;
};

/**
 * Sends a view_cart event to Google Tag Manager with the current basket contents.
 * 
 * @param basket - The current basket/cart data containing items and pricing
 */
const sendViewCartEvent = (basket: CommerceModel.Basket | undefined) => {
  // Extract basket financial data
  const { value, currency } = getBasketFinancials(basket);
  
  // Map basket items to GA4 format
  const items = basket?.lines?.map((line) => {
    const itemSelectionData = getSelectItemData(line.productId, line.productId);

    return productListItemToGAItem(line, {
      listId: itemSelectionData?.item_list_id,
      listName: itemSelectionData?.item_list_name,
      index: itemSelectionData?.index,
    });
  }) || [];
  
  sendGTMEvent({
    event: 'view_cart',
    ecommerce: {
      currency,
      value,
      items,
    },
  });
};
